import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon } from '@polichat/flamboyant';
import { toast } from 'react-toastify';

import { useGlotaT } from 'src/hooks/useGlotaTranslation';
import { WhatsAppCreateEditService } from '../../../../../services/tools/channels/whatsapp';

// Link da extensão Z-API Conector (Chrome Web Store) e do WhatsApp Web
const ZAPI_EXTENSION_URL =
  'https://chromewebstore.google.com/detail/iopmkohngoecmepoheapifkafbeomphn';
const WHATSAPP_WEB_URL = 'https://web.whatsapp.com/';

// Fallback: código Z-API expira em até 5 minutos
const DEFAULT_TTL_MS = 5 * 60 * 1000;

function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Painel do fluxo alternativo de conexão via extensão (Z-API Conector).
 * Exibido quando o cliente tem dificuldade para conectar pelo QR Code.
 * O código (XXXX-XXXX) é gerado no backend — o Client-Token nunca é exposto aqui.
 */
export default function ExtensionTokenPanel({ customerId, channelId, onBack }) {
  const title = useGlotaT('spa.certification.extensionTokenTitle');
  const description = useGlotaT('spa.certification.extensionTokenDescription');
  const step1 = useGlotaT('spa.certification.extensionTokenStep1');
  const step2 = useGlotaT('spa.certification.extensionTokenStep2');
  const step3 = useGlotaT('spa.certification.extensionTokenStep3');
  const installExtension = useGlotaT('spa.certification.extensionTokenInstall');
  const openWhatsAppWeb = useGlotaT('spa.certification.extensionTokenOpenWeb');
  const generatingText = useGlotaT('spa.certification.extensionTokenGenerating');
  const errorText = useGlotaT('spa.certification.extensionTokenError');
  const copyText = useGlotaT('spa.certification.extensionTokenCopy');
  const copiedText = useGlotaT('spa.certification.extensionTokenCopied');
  const regenerateText = useGlotaT('spa.certification.extensionTokenRegenerate');
  const expiredText = useGlotaT('spa.certification.extensionTokenExpired');
  const backToQrCode = useGlotaT('spa.certification.extensionTokenBackToQrCode');

  const [token, setToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);
  const [copied, setCopied] = useState(false);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const generateToken = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    setCopied(false);
    try {
      const result =
        await WhatsAppCreateEditService.extensionTokenWhatsAppZAPIChannel({
          customer_id: customerId,
          channel_customer_id: channelId,
        });

      if (!isMounted.current) return;

      if (result?.success && result?.token) {
        setToken(result.token);
        setExpiresAt(result.expiresAt ?? Date.now() + DEFAULT_TTL_MS);
      } else {
        setHasError(true);
        toast.error(result?.message || errorText);
      }
    } catch (error) {
      if (!isMounted.current) return;
      setHasError(true);
      toast.error(errorText);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [customerId, channelId, errorText]);

  // Gera o código ao montar o painel
  useEffect(() => {
    generateToken();
  }, [generateToken]);

  // Contagem regressiva de expiração do código
  useEffect(() => {
    if (!expiresAt) return undefined;

    const tick = () => {
      const diff = expiresAt - Date.now();
      if (isMounted.current) setRemainingMs(diff);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const isExpired = !!token && remainingMs <= 0;

  const handleCopy = useCallback(() => {
    if (!token) return;
    const done = () => {
      if (!isMounted.current) return;
      setCopied(true);
      setTimeout(() => isMounted.current && setCopied(false), 2000);
    };
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(token).then(done).catch(done);
    } else {
      done();
    }
  }, [token]);

  return (
    <div
      data-testid="extension-token-panel"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.5rem 1rem 1rem',
        textAlign: 'center',
      }}
    >
      <p style={{ margin: 0, color: 'var(--secondary-font-color)' }}>
        {description}
      </p>

      <ol
        style={{
          textAlign: 'left',
          margin: 0,
          paddingLeft: '1.2rem',
          color: 'var(--secondary-font-color)',
          fontSize: '0.9rem',
          lineHeight: 1.6,
        }}
      >
        <li>
          <a
            href={ZAPI_EXTENSION_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="extension-token-install-link"
          >
            {installExtension}
          </a>{' '}
          — {step1}
        </li>
        <li>
          <a
            href={WHATSAPP_WEB_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="extension-token-web-link"
          >
            {openWhatsAppWeb}
          </a>{' '}
          — {step2}
        </li>
        <li>{step3}</li>
      </ol>

      {isLoading && (
        <div
          data-testid="extension-token-loading"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Icon icon="poli-icon pi-spinner-line" size="24" />
          <span>{generatingText}</span>
        </div>
      )}

      {!isLoading && hasError && (
        <div data-testid="extension-token-error">
          <p style={{ color: 'var(--danger)' }}>{errorText}</p>
          <Button color="primary" size="md" onClick={generateToken}>
            {regenerateText}
          </Button>
        </div>
      )}

      {!isLoading && !hasError && token && (
        <>
          <div
            data-testid="extension-token-code"
            style={{
              fontSize: '2.2rem',
              fontWeight: 700,
              letterSpacing: '0.3rem',
              fontFamily: 'monospace',
              color: isExpired ? 'var(--gray)' : 'var(--primary)',
              userSelect: 'all',
            }}
          >
            {token}
          </div>

          {isExpired ? (
            <p
              data-testid="extension-token-expired"
              style={{ color: 'var(--danger)', margin: 0, fontWeight: 600 }}
            >
              {expiredText}
            </p>
          ) : (
            <p
              data-testid="extension-token-countdown"
              style={{ color: 'var(--secondary-font-color)', margin: 0 }}
            >
              {title}: {formatCountdown(remainingMs)}
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {!isExpired && (
              <Button
                color=""
                size="md"
                data-testid="extension-token-copy-button"
                style={{
                  borderColor: 'var(--gray)',
                  color: 'var(--primary-font-color)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
                onClick={handleCopy}
              >
                <Icon
                  icon={copied ? 'poli-icon pi-check-line' : 'poli-icon pi-copy-line'}
                  size="18"
                />
                {copied ? copiedText : copyText}
              </Button>
            )}
            <Button
              color="primary"
              size="md"
              data-testid="extension-token-regenerate-button"
              onClick={generateToken}
            >
              {regenerateText}
            </Button>
          </div>
        </>
      )}

      {onBack && (
        <Button
          color="link"
          size="sm"
          data-testid="extension-token-back-button"
          onClick={onBack}
        >
          {backToQrCode}
        </Button>
      )}
    </div>
  );
}
